"use client";

import { useCallback, useEffect, useState } from "react";
import { AdminAuthError, adminFetch } from "@/lib/admin-client";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { Badge, Button, Card, Spinner, inputClass } from "@/components/ui";

interface Template {
  id: string;
  name: string;
  description: string;
  tone: string;
  provider: { type: string; engine?: string; endpoint?: string };
  avatarIds: string[];
  voiceIds: string[];
  enabled: boolean;
  builtin: boolean;
}

const PROVIDER_OPTIONS: [string, string][] = [
  ["heygen", "HeyGen"],
  ["heygen_clone", "HeyGen Clone"],
  ["seedance", "Seedance · BytePlus ModelArk"],
  ["kickart", "Kickart"],
  ["minimax_h3", "MiniMax H3"],
  ["infinitetalk", "InfiniteTalk"],
  ["mock", "Mock · testing"],
];

const PROVIDER_LABELS = Object.fromEntries(PROVIDER_OPTIONS);
const PROVIDER_DEFAULT_ENGINES: Record<string, string> = {
  heygen: "avatar_iii",
  heygen_clone: "heygen_clone",
  seedance: "dreamina-seedance-2-0-260128",
  kickart: "kickart",
  minimax_h3: "minimax/h3-max/reference-to-video",
  infinitetalk: "fal-ai/infinitalk/single-text",
  mock: "mock",
};

export default function AdminTemplatesPage() {
  const [items, setItems] = useState<Template[] | null>(null);
  const [needKey, setNeedKey] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    tone: "",
    providerType: "heygen",
    engine: "avatar_iii",
    endpoint: "",
  });

  const load = useCallback(async () => {
    try {
      setItems(await adminFetch<Template[]>("/api/admin/templates"));
      setNeedKey(false);
    } catch (e) {
      if (e instanceof AdminAuthError) setNeedKey(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create() {
    setBusy("create");
    setNotice(null);
    try {
      await adminFetch("/api/admin/templates", {
        method: "POST",
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          tone: form.tone,
          provider: {
            type: form.providerType,
            engine: form.engine,
            endpoint: form.endpoint,
          },
        }),
      });
      setForm({ ...form, name: "", description: "", tone: "" });
      setNotice("Template created — it is live in the website wizard now");
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Create failed");
    } finally {
      setBusy(null);
    }
  }

  async function syncBuiltins() {
    setBusy("sync");
    setNotice(null);
    try {
      const result = await adminFetch<{ count: number }>(
        "/api/admin/templates/sync-builtins",
        { method: "POST" },
      );
      setNotice(
        result.count
          ? `${result.count} built-in video styles added`
          : "Built-in video styles are already up to date",
      );
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Sync failed");
    } finally {
      setBusy(null);
    }
  }

  async function toggle(t: Template) {
    setBusy(t.id);
    try {
      await adminFetch(`/api/admin/templates/${t.id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !t.enabled }),
      });
      await load();
    } finally {
      setBusy(null);
    }
  }

  async function remove(t: Template) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    setBusy(t.id);
    setNotice(null);
    try {
      await adminFetch(`/api/admin/templates/${t.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusy(null);
    }
  }

  if (needKey) return <AdminKeyGate onUnlock={() => void load()} />;
  if (!items) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          Workflow templates
        </h1>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy === "sync"}
          onClick={() => void syncBuiltins()}
        >
          {busy === "sync" ? <Spinner /> : "Sync built-in styles"}
        </Button>
      </div>
      <p className="mt-2 text-sm text-muted">
        Templates shown in the website wizard. Add or disable here and the
        site updates instantly; each template binds to an external render API.
      </p>

      {notice && (
        <Card className="mt-4 px-4 py-3 text-[13px] text-muted">{notice}</Card>
      )}

      <Card className="mt-6 p-5">
        <div className="mb-3 text-sm font-medium">New template</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            className={inputClass}
            placeholder="Name, e.g. Holiday Sale"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            className={inputClass}
            placeholder="Short description (shown to users)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <textarea
            className={`${inputClass} min-h-20 sm:col-span-2`}
            placeholder="Tone prompt for the script model, e.g. festive, urgent, gift-focused..."
            value={form.tone}
            onChange={(e) => setForm({ ...form, tone: e.target.value })}
          />
          <select
            className={inputClass}
            value={form.providerType}
            onChange={(e) => {
              const providerType = e.target.value;
              setForm({
                ...form,
                providerType,
                engine: PROVIDER_DEFAULT_ENGINES[providerType] ?? "",
              });
            }}
          >
            {PROVIDER_OPTIONS.map(([id, label]) => (
              <option key={id} value={id}>
                Provider: {label}
              </option>
            ))}
          </select>
          <input
            className={inputClass}
            placeholder="Engine, e.g. avatar_iii"
            value={form.engine}
            onChange={(e) => setForm({ ...form, engine: e.target.value })}
          />
          <input
            className={`${inputClass} sm:col-span-2`}
            placeholder="Custom endpoint (optional; credentials stay server-side)"
            value={form.endpoint}
            onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
          />
        </div>
        <Button
          className="mt-4"
          size="sm"
          disabled={busy === "create" || !form.name || !form.tone}
          onClick={() => void create()}
        >
          {busy === "create" ? <Spinner /> : "Add template"}
        </Button>
      </Card>

      <Card className="mt-6 divide-y divide-[var(--border)] p-0">
        {items.map((t) => (
          <div key={t.id} className="flex items-center gap-4 px-5 py-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{t.name}</span>
                <Badge tone={t.enabled ? "success" : "neutral"}>
                  {t.enabled ? "live" : "disabled"}
                </Badge>
                <Badge tone="accent">
                  {PROVIDER_LABELS[t.provider.type] ?? t.provider.type}
                  {t.provider.engine ? ` · ${t.provider.engine}` : ""}
                </Badge>
                {t.builtin && <Badge>built-in</Badge>}
              </div>
              <div className="mt-1 truncate text-[12px] text-muted">
                {t.description || t.tone}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy === t.id}
              onClick={() => void toggle(t)}
            >
              {t.enabled ? "Disable" : "Enable"}
            </Button>
            {!t.builtin && (
              <Button
                variant="secondary"
                size="sm"
                className="text-[var(--danger)]"
                disabled={busy === t.id}
                onClick={() => void remove(t)}
              >
                Delete
              </Button>
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}
